import {
  BATTLE_BADGE_IDS,
  BADGE_STAT_KEYS,
  BADGE_STAT_LABELS,
  effectsForBadge,
  isTowerBadge,
  type BadgeStatKey,
} from 'features/catalog/badge-definitions';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { RARITY_TIER_LABELS } from 'features/catalog/rarity';
import { RARITY_ORDER, type RarityName } from 'features/catalog/rarity-config';
import { ARENA_COMBOS, type ArenaCombo } from 'features/battle/arena-combos';
import {
  ARENA_MYTHIC_CHANCE,
  ARENA_SCHEDULE,
  ARENA_SINGLE_OPTIONS,
  ARENA_TRIO_OPTIONS,
} from 'features/battle/arena-config';
import { deckLimitBlocking, deckLimitExceeded } from 'features/battle/deck';
import { BATTLE_EFFECT_FILTERS, effectFiltersForBadge } from 'features/battle/effect-filters';
import { nextUnit, shuffleInPlace } from 'features/battle/engine';
import { badgeRarity, badgeStatsFor } from 'features/battle/rules';
import { SAMPLE_BATTLE_DECKS, type SampleBattleDeckId } from 'features/battle/sample-decks';
import { BATTLE_DECK_SIZE, type BattleOp } from 'features/battle/types';

export type ArenaTrio = { ids: [string, string, string]; label: string; combo?: string };
export type ArenaOffer =
  { kind: 'trio'; options: ArenaTrio[] } | { kind: 'single'; rarity: RarityName; options: string[] };

const SINGLE_BLOCK_RARITIES: RarityName[] = ['common', 'rare', 'legendary'];
const DOWNGRADE: Record<RarityName, RarityName | null> = {
  mythic: 'legendary',
  legendary: 'rare',
  rare: 'common',
  common: null,
};

export const arenaPool = (now = new Date()): string[] => BATTLE_BADGE_IDS.filter((id) => isBadgeReleased(id, now));

const deckRarities = (deck: readonly string[]): RarityName[] => deck.map(badgeRarity);

const fitsDeck = (deck: readonly string[], extra: readonly string[]): boolean => {
  if (extra.some((id, index) => deck.includes(id) || extra.indexOf(id) !== index)) return false;
  return deckLimitExceeded([...deckRarities(deck), ...extra.map(badgeRarity)]) == null;
};

const singleBlockRarities = (seed: number, block: 0 | 1): RarityName[] =>
  shuffleInPlace([...SINGLE_BLOCK_RARITIES], seed + 17 * (block + 1)).items;

const rarityForSinglePick = (pickIndex: number, seed: number): RarityName => {
  const block: 0 | 1 = pickIndex <= 3 ? 0 : 1;
  const offset = pickIndex <= 3 ? pickIndex - 1 : pickIndex - 5;
  return singleBlockRarities(seed, block)[offset] ?? 'common';
};

const legalRarity = (deck: readonly string[], rarity: RarityName): RarityName => {
  let current: RarityName | null = rarity;
  while (current && deckLimitBlocking(deckRarities(deck), current)) {
    current = DOWNGRADE[current];
  }
  return current ?? 'common';
};

const ofRarity = (pool: readonly string[], rarity: RarityName, used: ReadonlySet<string>): string[] =>
  pool.filter((id) => !used.has(id) && badgeRarity(id) === rarity);

const towerStatsKey = (id: string): string => BADGE_STAT_KEYS.map((key) => badgeStatsFor(id)[key]).join(',');

/** Same-stat towers are interchangeable, so a single pick never offers two of them. */
const takeCount = (ids: readonly string[], count: number, rng: number): { items: string[] | null; rng: number } => {
  if (ids.length < count) return { items: null, rng };
  const shuffled = shuffleInPlace([...ids], rng);
  const picked: string[] = [];
  const usedTowerStats = new Set<string>();
  for (const id of shuffled.items) {
    if (isTowerBadge(id)) {
      const key = towerStatsKey(id);
      if (usedTowerStats.has(key)) continue;
      usedTowerStats.add(key);
    }
    picked.push(id);
    if (picked.length === count) return { items: picked, rng: shuffled.rng };
  }
  return { items: null, rng: shuffled.rng };
};

const walkOps = (ops: readonly BattleOp[], visit: (op: BattleOp) => void): void => {
  for (const op of ops) {
    visit(op);
    if (op.kind === 'if') {
      walkOps(op.then, visit);
      walkOps(op.otherwise ?? [], visit);
    }
  }
};

/** A badge that only scores when this rarity is already on the board (Everything Bagel, etc.). */
export const requiredBoardRarity = (id: string): { rarity: RarityName; atLeast: number } | null => {
  let found: { rarity: RarityName; atLeast: number } | null = null;
  const visit = (op: BattleOp) => {
    if (op.kind === 'if' && op.condition.kind === 'board_rarity') {
      found = { rarity: op.condition.rarity, atLeast: op.condition.atLeast };
    }
  };
  for (const effect of effectsForBadge(id)) {
    walkOps(effect.ops, visit);
  }
  return found;
};

const rarityCount = (ids: readonly string[], rarity: RarityName): number =>
  ids.filter((id) => badgeRarity(id) === rarity).length;

const trioMeetsBoardNeed = (ids: readonly string[]): boolean =>
  ids.every((id) => {
    const need = requiredBoardRarity(id);
    return !need || rarityCount(ids, need.rarity) >= need.atLeast;
  });

const attachBoardNeed = (
  ids: readonly string[],
  pool: readonly string[],
  rng: number
): { items: string[] | null; rng: number } => {
  const picked = [...ids];
  let nextRng = rng;
  for (const id of ids) {
    const need = requiredBoardRarity(id);
    if (!need || rarityCount(picked, need.rarity) >= need.atLeast) continue;
    const missing = need.atLeast - rarityCount(picked, need.rarity);
    const partners = shuffleInPlace(
      pool.filter((candidate) => !picked.includes(candidate) && badgeRarity(candidate) === need.rarity),
      nextRng
    );
    nextRng = partners.rng;
    if (partners.items.length < missing) return { items: null, rng: nextRng };
    for (let i = 0; i < missing; i += 1) {
      const swapAt = picked.findIndex((card) => card !== id && badgeRarity(card) !== need.rarity);
      if (swapAt < 0) return { items: null, rng: nextRng };
      picked[swapAt] = partners.items[i];
    }
  }
  return { items: trioMeetsBoardNeed(picked) ? picked : null, rng: nextRng };
};

const sharedPrintedStats = (ids: readonly string[]): BadgeStatKey[] =>
  BADGE_STAT_KEYS.filter((key) => ids.every((id) => badgeStatsFor(id)[key] > 0));

const peakStat = (id: string): BadgeStatKey => {
  const stats = badgeStatsFor(id);
  return BADGE_STAT_KEYS.reduce((best, key) => (stats[key] > stats[best] ? key : best));
};

const sharedEffectVerbs = (ids: readonly string[]): string | null => {
  const sets = ids.map(effectFiltersForBadge);
  const shared = BATTLE_EFFECT_FILTERS.filter(
    (filter) => filter !== 'tower' && filter !== 'reveal' && sets.every((set) => set.has(filter))
  );
  if (shared.includes('prestige') && shared.includes('draw')) return 'Prestige Draws';
  if (shared.includes('prestige')) return 'Prestige';
  if (shared.includes('draw')) return 'Draws';
  if (shared.includes('activate')) return 'Activates';
  if (shared.includes('return')) return 'Bounces';
  if (shared.includes('replace')) return 'Swaps';
  if (shared.includes('boost')) return 'Boosts';
  return null;
};

const statNames = (keys: readonly BadgeStatKey[]): string =>
  keys
    .slice(0, 2)
    .map((key) => BADGE_STAT_LABELS[key])
    .join(' ');

const STANDALONE_STAT: Record<BadgeStatKey, string> = {
  culture: 'Culture Club',
  engineering: 'Engineering Bench',
  growth: 'Growth Spurt',
  fun: 'Fun Table',
  spotlight: 'In the Spotlight',
};

/** Title for a fallback trio, from shared stats / effects / rarity — never "Mixed". */
export const fallbackTrioLabel = (ids: readonly string[]): string => {
  const stats = sharedPrintedStats(ids);
  const towers = ids.filter(isTowerBadge).length;
  const verb = sharedEffectVerbs(ids);
  const rarities = ids.map(badgeRarity);
  const sameRarity = new Set(rarities).size === 1;
  const rarest = RARITY_ORDER.find((rarity) => rarities.includes(rarity));
  const rarityWord = sameRarity ? RARITY_TIER_LABELS[rarities[0]] : rarest === 'mythic' ? 'Mythic' : null;
  const statBit = stats.length ? statNames(stats) : null;

  if (towers === 3) return statBit ? `${statBit} Towers` : 'Stat Towers';
  if (statBit && verb) return `${statBit} ${verb}`;
  if (statBit && rarityWord) return `${rarityWord} ${statBit}`;
  if (statBit && stats.length === 1) return STANDALONE_STAT[stats[0]];
  if (statBit) return `${statBit} Board`;
  if (verb && rarityWord) return `${rarityWord} ${verb}`;
  if (verb === 'Prestige') return 'Chase Prestige';
  if (verb) return `${verb} Pack`;
  if (rarest === 'mythic') return 'Mythic Splash';
  if (sameRarity) return `Triple ${RARITY_TIER_LABELS[rarities[0]]}`;

  const peaks = [...new Set(ids.map(peakStat))];
  if (peaks.length === 1) return STANDALONE_STAT[peaks[0]];
  if (peaks.length === 2) return `${BADGE_STAT_LABELS[peaks[0]]} Meets ${BADGE_STAT_LABELS[peaks[1]]}`;
  return `${BADGE_STAT_LABELS[peaks[0]]}, ${BADGE_STAT_LABELS[peaks[1]]} & ${BADGE_STAT_LABELS[peaks[2]]}`;
};

const asTrio = (ids: string[], combo?: ArenaCombo): ArenaTrio | null => {
  if (ids.length !== 3) return null;
  if (combo) return { ids: [ids[0], ids[1], ids[2]], label: combo.label, combo: combo.id };
  return { ids: [ids[0], ids[1], ids[2]], label: fallbackTrioLabel(ids) };
};

const comboLine = (combo: ArenaCombo): string => combo.line ?? combo.id;

const pickAuthoredTrios = (
  deck: readonly string[],
  pool: readonly string[],
  used: Set<string>,
  rng: number
): ArenaTrio[] => {
  const shuffled = shuffleInPlace([...ARENA_COMBOS], rng);
  const trios: ArenaTrio[] = [];
  const lines = new Set<string>();
  for (const combo of shuffled.items) {
    const line = comboLine(combo);
    if (lines.has(line)) continue;
    if (combo.ids.some((id) => !pool.includes(id) || used.has(id) || deck.includes(id))) continue;
    if (!fitsDeck(deck, combo.ids) || !trioMeetsBoardNeed(combo.ids)) continue;
    const trio = asTrio([...combo.ids], combo);
    if (!trio) continue;
    combo.ids.forEach((id) => used.add(id));
    lines.add(line);
    trios.push(trio);
    if (trios.length >= ARENA_TRIO_OPTIONS) break;
  }
  return trios;
};

const fallbackTrio = (
  deck: readonly string[],
  pool: readonly string[],
  used: Set<string>,
  rng: number
): ArenaTrio | null => {
  const available = shuffleInPlace(
    pool.filter((id) => !used.has(id) && !deck.includes(id)),
    rng
  ).items;
  const picked: string[] = [];
  const rarities = deckRarities(deck);
  for (const id of available) {
    if (deckLimitBlocking([...rarities, ...picked.map(badgeRarity)], badgeRarity(id))) continue;
    picked.push(id);
    if (picked.length === 3) {
      const completed = attachBoardNeed(picked, available, rng);
      const trioIds = completed.items ?? picked;
      if (!fitsDeck(deck, trioIds) || !trioMeetsBoardNeed(trioIds)) return null;
      trioIds.forEach((badgeId) => used.add(badgeId));
      return asTrio(trioIds);
    }
  }
  return null;
};

export const nextArenaOffer = (deck: string[], pickIndex: number, seed: number): ArenaOffer => {
  const kind = ARENA_SCHEDULE[pickIndex] ?? 'single';
  const shuffledPool = shuffleInPlace(arenaPool(), seed + pickIndex * 11);
  const pool = shuffledPool.items;
  const used = new Set(deck);
  if (kind === 'single') {
    const wanted = legalRarity(deck, rarityForSinglePick(pickIndex, seed));
    let rarity = wanted;
    let options = takeCount(ofRarity(pool, rarity, used), ARENA_SINGLE_OPTIONS, seed + pickIndex * 97).items;
    if (!options && wanted !== 'common') {
      rarity = legalRarity(deck, DOWNGRADE[wanted] ?? 'common');
      options = takeCount(ofRarity(pool, rarity, used), ARENA_SINGLE_OPTIONS, seed + pickIndex * 99).items;
    }
    if (!options) {
      const fallback = takeCount(
        pool.filter((id) => !used.has(id) && !deckLimitBlocking(deckRarities(deck), badgeRarity(id))),
        ARENA_SINGLE_OPTIONS,
        seed + pickIndex
      ).items;
      if (!fallback) throw new Error('Arena draft: not enough badges to offer.');
      return { kind: 'single', rarity: badgeRarity(fallback[0]), options: fallback };
    }
    if (wanted === 'legendary' && rarity === 'legendary' && !deck.some((id) => badgeRarity(id) === 'mythic')) {
      const mythicChance = nextUnit(seed + pickIndex * 333);
      if (mythicChance.value < ARENA_MYTHIC_CHANCE) {
        const mythics = shuffleInPlace(ofRarity(pool, 'mythic', used), seed + pickIndex * 401);
        if (mythics.items.length && !deckLimitBlocking(deckRarities(deck), 'mythic')) {
          const slot = nextUnit(mythics.rng);
          const swap = Math.floor(slot.value * options.length);
          options = [...options];
          options[swap] = mythics.items[0];
        }
      }
    }
    return { kind: 'single', rarity, options };
  }

  const poolTrios = pickAuthoredTrios(deck, pool, used, seed + pickIndex * 41);
  while (poolTrios.length < ARENA_TRIO_OPTIONS) {
    const fallback = fallbackTrio(deck, pool, used, seed + pickIndex * 7 + poolTrios.length);
    if (!fallback) break;
    poolTrios.push(fallback);
  }
  if (poolTrios.length < ARENA_TRIO_OPTIONS) throw new Error('Arena draft: could not build trio options.');
  return { kind: 'trio', options: poolTrios };
};

export const arenaOpponentQueue = (seed: number): SampleBattleDeckId[] =>
  shuffleInPlace(
    SAMPLE_BATTLE_DECKS.map((deck) => deck.id),
    seed || 1
  ).items;

export const legendaryOrRarerCount = (deck: readonly string[]): number =>
  deck.filter((id) => badgeRarity(id) === 'legendary' || badgeRarity(id) === 'mythic').length;

export const arenaDeckIsLegal = (deck: readonly string[]): boolean =>
  deck.length === BATTLE_DECK_SIZE &&
  new Set(deck).size === BATTLE_DECK_SIZE &&
  deckLimitExceeded(deckRarities(deck)) == null &&
  legendaryOrRarerCount(deck) >= 2;
